import Vue from 'vue';
import { mapState, mapActions, mapMutations } from 'vuex';
import { debounce } from 'vue-debounce';
import { navigateWindow } from '@/router/routerUtils';
import { $httpChangiRecommends } from '@/core/api';
import Swiper from '@/components/swiper/Swiper.vue';
import SearchBar from '../../modules/header/components/SearchBar.vue';
export default Vue.extend({
    components: { Swiper, SearchBar },
    props: {
        banners: {
            type: Array,
            default: [],
        },
        delay: {
            type: Number,
            default: 4,
        },
        showSearch: {
            type: Boolean,
            default: false,
        },
        showNavigation: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isNavSearchActive: false,
            searchText: '',
            searchResult: null,
            searchLoading: false,
            // slide words (Klook-like)
            slideWords: ['universal studios singapore', 'jewel changi airport', 'singapore zoo'],
            slideWordIndex: 0,
            slideWordTimer: null,
            slideWordNoTransition: false,
            slideWordH: 22,
            swiperOptions: {
                navigation: this.showNavigation
                    ? {
                        nextEl: '.swiper-button-next',
                        prevEl: '.swiper-button-prev',
                    }
                    : false,
                pagination: {
                    el: '.swiper-pagination',
                    clickable: true,
                },
                slidesPerView: 1,
                slidesPerGroup: 1,
                loop: true,
                watchOverflow: true,
                autoplay: true,
                autoHeight: false,
            },
        };
    },
    computed: {
        ...mapState('searchModule', ['query', 'recentSearches']),
        // render list includes a duplicate first item for seamless loop
        slideWordRenderList() {
            if (!this.slideWords || this.slideWords.length === 0)
                return [];
            return [...this.slideWords, this.slideWords[0]];
        },
        // show slide words only when the input is "empty and inactive"
        showSlideWords() {
            return !this.isNavSearchActive && !this.searchText;
        },
        // translate track up by index * height
        slideWordTrackStyle() {
            return {
                transform: `translate3d(0, -${this.slideWordIndex * this.slideWordH}px, 0)`,
            };
        },
    },
    watch: {
        query(current) {
            if (current)
                this.searchText = current;
        },
        // pause/resume sliding based on focus/typing
        showSlideWords(now) {
            if (now)
                this.startSlideWords();
            else
                this.stopSlideWords();
        },
        // stop timer if showSearch toggles off
        showSearch(now) {
            if (!now)
                this.stopSlideWords();
            else if (this.showSlideWords)
                this.startSlideWords();
        },
    },
    created() {
        // Keep text in sync if user landed from /search/:query
        if (window.location.pathname.includes('search/')) {
            this.searchText = this.query;
        }
    },
    mounted() {
        if (this.showSearch && this.showSlideWords && !this.$root?.simple_layout) {
            this.startSlideWords();
        }
    },
    beforeDestroy() {
        this.stopSlideWords();
    },
    methods: {
        ...mapMutations('searchModule', ['setQuery', 'clearRecentSearches']),
        ...mapActions('categoriesModule', ['getCategories']),
        // start/stop sliding words (bottom-to-top, pause)
        startSlideWords() {
            if (this.slideWordTimer || !this.slideWords || this.slideWords.length < 2)
                return;
            const stepMs = 1800; // pause duration per word
            const animMs = 450; // transition duration (keep in sync with CSS)
            this.slideWordTimer = window.setInterval(() => {
                // move to next (includes duplicate last item)
                this.slideWordNoTransition = false;
                this.slideWordIndex += 1;
                // if we reached the duplicate slide (index === slideWords.length),
                // jump back to 0 *without* transition right after the animation completes.
                if (this.slideWordIndex === this.slideWords.length) {
                    window.setTimeout(() => {
                        this.slideWordNoTransition = true;
                        this.slideWordIndex = 0;
                        // re-enable transitions on the next tick
                        window.setTimeout(() => {
                            this.slideWordNoTransition = false;
                        }, 16);
                    }, animMs);
                }
            }, stepMs);
        },
        stopSlideWords() {
            if (this.slideWordTimer) {
                window.clearInterval(this.slideWordTimer);
                this.slideWordTimer = null;
            }
            // optional: reset to first word when hidden
            this.slideWordNoTransition = true;
            this.slideWordIndex = 0;
            window.setTimeout(() => {
                this.slideWordNoTransition = false;
            }, 16);
        },
        clearSearch() {
            this.searchResult = null;
            this.searchText = '';
            this.closeNavSearch();
        },
        openNavSearch() {
            this.isNavSearchActive = true;
        },
        closeNavSearch() {
            this.isNavSearchActive = false;
        },
        goToSearchPage() {
            navigateWindow(`search/${encodeURIComponent(this.searchText)}`);
        },
        // Copy of your header search logic (kept minimal)
        searchTextChanged: debounce(function (value) {
            this.searchLoading = true;
            if (!value) {
                this.searchResult = null;
                this.searchLoading = false;
                return;
            }
            if (value.length < 3) {
                this.searchLoading = false;
                return;
            }
            this.setQuery(value);
            $httpChangiRecommends.api
                .ChangiRecommendsProductSearch({
                query: encodeURIComponent(value),
                autocompleteOnly: true,
            })
                .then((resp) => {
                this.searchResult = resp;
            })
                .catch((error) => {
                this.searchResult = { data: [], message: error.message };
                this.searchLoading = false;
            })
                .finally(() => {
                this.searchLoading = false;
            });
        }, 400),
    },
});
